import type { SpeciesData } from '../types';
import i18next from 'i18next';

export type SpeciesTargetability = 'targetable' | 'incidental' | 'reference_only';

export interface SpeciesTargetabilityMeta {
  label: string;
  shortLabel: string;
  icon: string;
  description: string;
  badgeClassName: string;
}

const INCIDENTAL_PATTERNS = [
  /anchovy/,
  /pilchard/,
  /sardine/,
  /sand eel/
];

const REFERENCE_ONLY_PATTERNS = [
  /flying fish/,
  /exocoetidae/,
  /cheilopogon/
];

const TARGETABILITY_META_STYLE: Record<SpeciesTargetability, Pick<SpeciesTargetabilityMeta, 'icon' | 'badgeClassName'>> = {
  targetable: {
    icon: '🎯',
    badgeClassName: 'bg-mediterranean-green-50/90 text-mediterranean-green-800 border border-mediterranean-green-200/70'
  },
  incidental: {
    icon: '🪱',
    badgeClassName: 'bg-mediterranean-gold-50/90 text-mediterranean-gold-800 border border-mediterranean-gold-200/70'
  },
  reference_only: {
    icon: '📚',
    badgeClassName: 'bg-neutral-100/95 text-neutral-700 border border-neutral-200/80'
  }
};

const tTargetability = (targetability: SpeciesTargetability, key: 'label' | 'shortLabel' | 'description') =>
  String(i18next.t(`codex:speciesTargetability.${targetability}.${key}`));

export const inferSpeciesTargetability = (species: Pick<SpeciesData, 'speciesId' | 'name' | 'scientificName' | 'fishingTechniqueIds'> & { targetability?: SpeciesTargetability }): SpeciesTargetability => {
  if (species.targetability) {
    return species.targetability;
  }

  const haystack = `${species.name || ''} ${species.scientificName || ''} ${species.speciesId || ''}`.toLowerCase();

  if (REFERENCE_ONLY_PATTERNS.some((pattern) => pattern.test(haystack))) {
    return 'reference_only';
  }

  if (!species.fishingTechniqueIds || species.fishingTechniqueIds.length === 0) {
    return 'reference_only';
  }

  if (INCIDENTAL_PATTERNS.some((pattern) => pattern.test(haystack))) {
    return 'incidental';
  }

  return 'targetable';
};

export const getSpeciesTargetabilityMeta = (targetability: SpeciesTargetability): SpeciesTargetabilityMeta =>
  ({
    label: tTargetability(targetability, 'label'),
    shortLabel: tTargetability(targetability, 'shortLabel'),
    description: tTargetability(targetability, 'description'),
    ...TARGETABILITY_META_STYLE[targetability]
  });

export const isSpeciesRecommendationEligible = (species: Pick<SpeciesData, 'speciesId' | 'name' | 'scientificName' | 'fishingTechniqueIds'> & { targetability?: SpeciesTargetability }): boolean =>
  inferSpeciesTargetability(species) === 'targetable';

export const isSpeciesReferenceOnly = (species: Pick<SpeciesData, 'speciesId' | 'name' | 'scientificName' | 'fishingTechniqueIds'> & { targetability?: SpeciesTargetability }): boolean =>
  inferSpeciesTargetability(species) === 'reference_only';

export const withSpeciesTargetability = <T extends Pick<SpeciesData, 'speciesId' | 'name' | 'scientificName' | 'fishingTechniqueIds'> & { targetability?: SpeciesTargetability }>(species: T): T & { targetability: SpeciesTargetability } => ({
  ...species,
  targetability: inferSpeciesTargetability(species)
});
